(() => {
  const fail = message => Object.assign(new Error(message), {publicMessage: message});
  const counts = ids => ids.reduce((m, id) => m.set(String(id), (m.get(String(id)) || 0) + 1), new Map());
  const buildPlan = (catalog, request, groupId) => {
    const tier = catalog.tiers.find(t => t.format === request.format && t.count === request.count);
    if (!tier || request.quantity !== 1 || request.selectedVariantIds.length !== tier.count || request.expectedPriceInCents !== tier.price) throw fail(catalog.strings.invalid);
    const preparation = catalog.preparations.find(p => p.format === tier.format);
    if (!preparation) throw fail(catalog.strings.unavailable);
    const selected = request.selectedVariantIds.map(id => catalog.bottles.find(b => b.id === String(id) && b.format === tier.format));
    if (selected.some(b => !b) || selected.reduce((sum, b) => sum + b.unitPrice, preparation.price) !== tier.price) throw fail(catalog.strings.priceChanged);
    const properties = {_lm_coffret: groupId, _lm_format: String(tier.format), _lm_count: String(tier.count), _lm_total: String(tier.price)};
    const parent = {id: Number(preparation.id), quantity: 1, properties: {...properties, _lm_role: 'preparation', Coffret: `${tier.count} × ${tier.format} ml`, Composition: selected.map(b => b.label).join(' · ')}};
    const children = [...counts(request.selectedVariantIds)].map(([id, quantity]) => ({id: Number(id), quantity, properties: {...properties, _lm_role: 'bottle', Coffret: `${tier.count} × ${tier.format} ml`}}));
    return {parent, children, preparation, tier, selected};
  };
  const makeBackend = (catalog, requestJSON, enabled) => {
    const getProduct = async handle => {
      const data = await requestJSON(`products/${encodeURIComponent(handle)}?view=coffret-stock&_lm=${Date.now()}`);
      if (!Array.isArray(data.variants)) throw fail(catalog.strings.unavailable);
      return data;
    };
    const rollback = async groupId => {
      const cart = await requestJSON('cart.js');
      const updates = Object.fromEntries(cart.items.filter(i => i.properties?._lm_coffret === groupId).map(i => [i.key, 0]));
      if (Object.keys(updates).length) await requestJSON('cart/update.js', {updates});
    };
    return {
      ready: enabled,
      async refreshCatalog() {
        if (!enabled) return;
        const handles = [...new Set(catalog.bottles.map(b => b.handle))];
        let loaded = 0;
        let index = 0;
        await Promise.all(Array.from({length: Math.min(4, handles.length)}, async () => {
          while (index < handles.length) {
            const handle = handles[index++];
            try {
              const product = await getProduct(handle);
              loaded++;
              catalog.bottles.filter(b => b.handle === handle).forEach(b => {
                const v = product.variants.find(v => String(v.id) === b.id);
                b.stockSnapshot = v?.available && v.tracked ? Math.max(0, v.stock) : v?.available ? 999 : 0;
              });
            } catch {catalog.bottles.filter(b => b.handle === handle).forEach(b => {b.stockSnapshot = 0;});}
          }
        }));
        if (!loaded) throw fail(catalog.strings.unavailable);
        return catalog;
      },
      async addBundle(request) {
        if (!enabled) throw fail(catalog.strings.unavailable);
        const groupId = crypto.randomUUID();
        const plan = buildPlan(catalog, request, groupId);
        const before = await requestJSON('cart.js');
        if (before.currency !== 'EUR') throw fail(catalog.strings.currency);
        const handles = [...new Set([plan.preparation.handle, ...plan.selected.map(b => b.handle)])];
        const products = await Promise.all(handles.map(getProduct));
        const live = new Map(products.flatMap(p => p.variants).map(v => [String(v.id), v]));
        for (const item of [plan.parent, ...plan.children]) {
          const variant = live.get(String(item.id));
          const existing = before.items.filter(i => String(i.variant_id) === String(item.id)).reduce((n, i) => n + i.quantity, 0);
          if (!variant?.available || variant.tracked && variant.stock < existing + item.quantity) throw fail(catalog.strings.stock);
          const price = String(item.id) === plan.preparation.id ? plan.preparation.price : plan.selected.find(b => b.id === String(item.id)).unitPrice;
          if (variant.price !== price) throw fail(catalog.strings.priceChanged);
        }
        let started = false;
        try {
          started = true;
          await requestJSON('cart/add.js', {items: [plan.parent]});
          const first = await requestJSON('cart.js');
          const parent = first.items.find(i => i.properties?._lm_coffret === groupId && i.properties._lm_role === 'preparation');
          if (!parent?.key) throw fail(catalog.strings.cartError);
          await requestJSON('cart/add.js', {items: plan.children.map(i => ({...i, parent_line_key: parent.key}))});
          const cart = await requestJSON('cart.js');
          const group = cart.items.filter(i => i.properties?._lm_coffret === groupId);
          const actualParent = group.find(i => i.properties._lm_role === 'preparation');
          const actualChildren = group.filter(i => i.properties._lm_role === 'bottle');
          const expected = counts(request.selectedVariantIds);
          const total = group.reduce((sum, i) => sum + i.original_line_price, 0);
          const valid = cart.currency === 'EUR' && actualParent?.quantity === 1 && actualChildren.length === expected.size && [...expected].every(([id, qty]) => actualChildren.some(i => String(i.variant_id) === id && i.quantity === qty && i.parent_relationship?.parent_key === actualParent.key));
          if (!valid || total !== plan.tier.price) throw fail(catalog.strings.cartError);
          return {verified: true, groupId};
        } catch (error) {
          if (started) {
            try {await rollback(groupId);} catch {throw fail(catalog.strings.rollback);}
          }
          throw error;
        }
      }
    };
  };
  const validateCart = (catalog, cart) => {
    const grouped = cart.items.filter(i => i.properties?._lm_coffret);
    const groupIds = new Set(grouped.map(i => i.properties._lm_coffret));
    if (!groupIds.size) return true;
    if (cart.currency !== 'EUR') return false;
    return [...groupIds].every(id => {
      const group = grouped.filter(i => i.properties._lm_coffret === id);
      const parents = group.filter(i => i.properties._lm_role === 'preparation');
      if (parents.length !== 1 || parents[0].quantity !== 1) return false;
      const parent = parents[0], format = Number(parent.properties._lm_format), count = Number(parent.properties._lm_count);
      const tier = catalog.tiers.find(t => t.format === format && t.count === count);
      const preparation = catalog.preparations.find(p => p.format === format);
      if (!tier || !preparation || String(parent.variant_id) !== preparation.id || parent.original_line_price !== preparation.price) return false;
      const children = group.filter(i => i.properties._lm_role === 'bottle');
      if (group.length !== children.length + 1 || children.reduce((n, i) => n + i.quantity, 0) !== count) return false;
      return children.every(i => {
        const b = catalog.bottles.find(b => b.id === String(i.variant_id) && b.format === format);
        return b && i.parent_relationship?.parent_key === parent.key && i.original_line_price === b.unitPrice * i.quantity;
      }) && group.reduce((n, i) => n + i.original_line_price, 0) === tier.price;
    });
  };
  window.LeManuscrisCoffretNative = {buildPlan, makeBackend, validateCart};
  document.querySelectorAll('lm-coffret').forEach(root => {
    const config = root.querySelector('[data-coffret-native]');
    if (!config) return;
    const settings = JSON.parse(config.textContent);
    const catalogPromise = window.LeManuscrisCoffretCatalog.load(root.dataset.catalog, {rootUrl: root.dataset.rootUrl, previewThemeId: root.dataset.previewThemeId});
    const requestJSON = async (path, body) => {
      const productView = path.startsWith('products/');
      if (productView && root.dataset.previewThemeId) path += '&preview_theme_id=' + encodeURIComponent(root.dataset.previewThemeId);
      const response = await fetch(root.dataset.rootUrl.replace(/\/?$/, '/') + path, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Content-Type': 'application/json', Accept: productView ? 'text/html' : 'application/json'}, ...(body ? {body: JSON.stringify(body)} : {})});
      let json;
      try {json = await response.json();} catch {throw fail(settings.strings.unavailable);}
      if (!response.ok) throw fail(response.status === 422 ? settings.strings.stock : settings.strings.cartError);
      return json;
    };
    catalogPromise.then(catalog => {
      catalog.strings = settings.strings;
      const backend = makeBackend(catalog, requestJSON, settings.enabled);
      root.nativeBackend = backend;
      root.dispatchEvent(new Event('lm:coffret:backend-ready'));
    }).catch(() => {});
  });
})();
